/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

/**
 * AI Usage Contract for oRPC
 *
 * Provides type-safe contract for AI token usage analytics endpoints.
 * Exposes aggregated AI usage data stored in AiUsageRecord.
 */
import { oc } from "@orpc/contract";
import * as z from "zod";
import { DashboardChannelSchema } from "./dashboard.contract";

// ============================================================================
// AI Usage Schemas
// ============================================================================

/**
 * Per-model usage breakdown
 */
export const ModelUsageSchema = z.object({
	model: z.string(),
	inputTokens: z.number(),
	outputTokens: z.number(),
	totalTokens: z.number(),
	totalCostUsd: z.number(),
	recordCount: z.number(),
	/**
	 * Cache-read input tokens for this model (prompt tokens served from the
	 * provider's prompt cache). Sourced from the non-billing
	 * ai_generation_metric telemetry table; `0` when no telemetry rows exist
	 * for the model. `.default(0)` keeps older cached responses valid.
	 */
	cachedInputTokens: z.number().default(0),
	/** Cache-write input tokens (prompt tokens written into the cache). */
	cacheWriteInputTokens: z.number().default(0),
	/** Reasoning / extended-thinking output tokens. */
	reasoningTokens: z.number().default(0),
});

/** Reporting-only usage breakdown by agent preset. */
export const PresetUsageSchema = z.object({
	presetId: z.string().nullable(),
	presetName: z.string().nullable(),
	inputTokens: z.number(),
	outputTokens: z.number(),
	totalTokens: z.number(),
	totalCostUsd: z.number(),
	recordCount: z.number(),
});

/**
 * Per-user usage breakdown (rows for users in the org who issued AI calls).
 * `userId === null` represents calls with no attached user (the autonomous
 * WhatsApp assistant / system flows). `name` and `email` are resolved
 * server-side by joining the org roster so paginated rows carry their own
 * identity (the client no longer resolves ids against a full-roster query).
 */
export const UserUsageSchema = z.object({
	userId: z.string().nullable(),
	/** Display name from the User row; null for the assistant bucket or an
	 * unresolved/deleted user. */
	name: z.string().nullable().default(null),
	/** Email from the User row; null for the assistant bucket or a user
	 * without an email on file. */
	email: z.string().nullable().default(null),
	inputTokens: z.number(),
	outputTokens: z.number(),
	totalTokens: z.number(),
	totalCostUsd: z.number(),
	recordCount: z.number(),
});

/**
 * Per-day usage breakdown (one row per organization-local day in the queried window).
 * Used by the dashboard chart.
 */
export const DailyUsageSchema = z.object({
	/** ISO 8601 organization-local day bucket (serialized at midnight). */
	date: z.string().datetime(),
	inputTokens: z.number(),
	outputTokens: z.number(),
	totalTokens: z.number(),
	totalCostUsd: z.number(),
	recordCount: z.number(),
});

/**
 * AI SDK generation telemetry summary. These fields come from the
 * non-billing ai_generation_metric table and are best-effort: providers may
 * omit usage or timing data.
 */
export const AiUsagePerformanceSummarySchema = z.object({
	totalGenerations: z.number(),
	missingUsageCount: z.number(),
	missingPerformanceCount: z.number(),
	toolCallCount: z.number(),
	toolResultCount: z.number(),
	toolErrorCount: z.number(),
	averageResponseTimeMs: z.number().nullable(),
	p95ResponseTimeMs: z.number().nullable(),
	averageStepTimeMs: z.number().nullable(),
	averageTimeToFirstOutputMs: z.number().nullable(),
	averageOutputTokensPerSecond: z.number().nullable(),
	averageEffectiveOutputTokensPerSecond: z.number().nullable(),
	totalToolExecutionMs: z.number(),
	maxToolExecutionMs: z.number().nullable(),
	costPerResponseUsd: z.number().nullable(),
	costPer1kTokensUsd: z.number().nullable(),
	totalToolCalls: z.number(),
	totalToolErrors: z.number(),
	toolErrorRate: z.number().nullable(),
});

/**
 * Aggregated AI usage response
 */
export const AiUsageSummarySchema = z.object({
	/** Total input tokens across all models */
	totalInputTokens: z.number(),
	/** Total output tokens across all models */
	totalOutputTokens: z.number(),
	/** Total tokens across all models */
	totalTokens: z.number(),
	/** Total cost in USD across all models */
	totalCostUsd: z.number(),
	/** Total number of AI usage records */
	totalRecords: z.number(),
	/** Per-model breakdown */
	byModel: z.array(ModelUsageSchema),
	/** Per-preset breakdown. Deleted presets retain rows with a null name. */
	byPreset: z.array(PresetUsageSchema).default([]),
	/**
	 * Per-user breakdown — a SINGLE PAGE of the top spenders (ordered by cost
	 * desc), not the whole org. Empty when `groupBy.user` is false. Paginate
	 * with the `userLimit` / `userOffset` request params.
	 */
	byUser: z.array(UserUsageSchema).default([]),
	/** Total distinct users (incl. the assistant bucket) with spend in the
	 * period — the denominator for `byUser` pagination. 0 when users aren't
	 * requested. */
	byUserTotal: z.number().default(0),
	/** Highest single-user spend in the period (the #1 spender's total), so
	 * the client can scale per-member bars consistently across pages. */
	byUserMaxCostUsd: z.number().default(0),
	/**
	 * Per-day breakdown across the queried period. Empty when
	 * `groupBy.day` is false. Used to render the daily-cost chart.
	 */
	byDay: z.array(DailyUsageSchema).default([]),
	/** Best-effort AI SDK performance telemetry. */
	performance: AiUsagePerformanceSummarySchema,
	/**
	 * Linear end-of-period projection in USD, ONLY populated when
	 * `period='month'` and the month is in progress. Computed as
	 * `currentTotal * daysInMonth / daysElapsed`. Null otherwise.
	 */
	projectedMonthlyTotalUsd: z.number().nullable().default(null),
	/** The org's plan AI cost cap in USD (for progress-bar UI). */
	planLimitUsd: z.number().default(0),
	/** Period start date (ISO 8601) */
	periodStart: z.string(),
	/** Period end date (ISO 8601) */
	periodEnd: z.string(),
});

// Input schema
export const AiUsageQueryInputSchema = z.object({
	/** Period: 'month' for current month, 'all' for all time */
	period: z.enum(["month", "all"]).optional().default("month"),
	/** Dashboard-only exact local-date range; both override period when supplied. */
	from: z.string().max(30).optional(),
	to: z.string().max(30).optional(),
	channel: DashboardChannelSchema.optional(),
	/**
	 * Optional dimensional breakdowns to include in the response.
	 * `model` is always included; `user` and `day` are opt-in to
	 * keep the default response light for legacy callers.
	 */
	groupBy: z
		.object({
			user: z.boolean().optional().default(false),
			day: z.boolean().optional().default(false),
		})
		.optional()
		.default(() => ({ user: false, day: false })),
	/** Page size for the `byUser` breakdown (top-N spenders). Defaults to 5. */
	userLimit: z.number().int().min(1).max(100).optional().default(5),
	/** Row offset into the `byUser` breakdown (page index × page size). */
	userOffset: z.number().int().min(0).optional().default(0),
});

// Define errors
const aiUsageErrors = {
	UNAUTHORIZED: {
		message: "Authentication required to access AI usage data",
	},
	DATABASE_ERROR: {
		message: "Failed to retrieve AI usage data",
		data: z.object({
			code: z.string().optional(),
		}),
	},
};

/**
 * AI Usage Contract
 *
 * Endpoints:
 * - GET /api/ai-usage - Get aggregated AI usage analytics
 */
export const aiUsageContract = {
	get: oc
		.route({
			method: "GET",
			path: "/api/ai-usage",
			summary: "Get AI token usage analytics",
			description:
				"Returns aggregated AI token usage data including per-model breakdown, costs, and totals.",
			tags: ["AI Usage"],
		})
		.input(AiUsageQueryInputSchema)
		.output(AiUsageSummarySchema)
		.errors(aiUsageErrors),
};

// Export types
export type AiUsageContract = typeof aiUsageContract;
export type AiUsageQueryInput = z.infer<typeof AiUsageQueryInputSchema>;
export type AiUsageSummary = z.infer<typeof AiUsageSummarySchema>;
export type ModelUsage = z.infer<typeof ModelUsageSchema>;
export type PresetUsage = z.infer<typeof PresetUsageSchema>;
export type AiUsagePerformanceSummary = z.infer<
	typeof AiUsagePerformanceSummarySchema
>;
